import { computed, DestroyRef, inject, Injectable, signal, Signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExplorerFolder, FolderContext } from '@app/shared/types/models/files/explorer-folder.model';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { DataStore } from '@app/store/data-store';
import { defer, filter, finalize, map, Observable, Subscription, tap, throwError } from 'rxjs';
import { FileContext } from '../types/enums/file-enums';
import { UploadData } from '../types/models/files/upload-data.model';
import { AppEvent } from '../types/models/notifications/app-event';
import { ActiveCompanyService } from './active-company.service';
import { DataService } from './data-service';
import { FolderDTO, FolderFileDTO } from './dtos/folders/folder.dto';
import { FolderFactory } from './factories/folder.factory';
import { FileDataService } from './file-data.service';
import { FileHttpService } from './http/file-http.service';
import { FolderHttpService } from './http/folder-http.service';
import { SessionScopeService } from './session-scope.service';
import { TasksDataService } from './tasks-data.service';

export interface FolderLoadState {
  loading: boolean;
  loaded: boolean;
  error?: string;
}

const INITIAL_STATE: FolderLoadState = { loading: false, loaded: false };

/**
 * PROPOZYCJA — DO WERYFIKACJI: owns folder metadata, request state and attachment IDs only.
 * Existing FileDataService owns every FileInfo; metadata returned for a child is not loaded content.
 * Scope: root, with company-scoped request keys and a cache invalidated when the signed-in user or the active
 * company changes ({@link SessionScopeService}); an access-token refresh keeps the cache and in-flight results.
 * No parallel cache of files.
 */
@Injectable({ providedIn: 'root' })
export class FolderDataService extends DataService<FolderDTO, ExplorerFolder> {
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(FolderHttpService);
  private readonly factory = inject(FolderFactory);
  private readonly fileHttp = inject(FileHttpService);
  private readonly tasks = inject(TasksDataService);
  private readonly fileData = inject(FileDataService);
  private readonly company = inject(ActiveCompanyService).activeCompanyId;
  private readonly sessionKey = inject(SessionScopeService).key;
  private readonly requests = new Map<string, Subscription>();
  private readonly requestVersions = new Map<string, number>();
  private readonly stateVersions = new Map<string, number>();
  private readonly contentVersions = new Map<string, number>();
  // Replaced (not written) on a session change, see ensureSession().
  private folders = new DataStore<ExplorerFolder>();
  private roots = signal(new Map<string, string>());
  private states = signal(new Map<string, FolderLoadState>());
  private session: string | undefined;
  private nextVersion = 0;

  protected override handleEvent(event: AppEvent): void {
    // The base constructor can replay an event before derived fields are initialized; no cache exists then.
    if (!this.roots || event.eventType !== 'TaskFilesChanged') return;
    const data = event.data as { taskId?: string; companyId?: string } | undefined;
    if (!data?.taskId || !data.companyId || data.companyId !== this.company()) return;
    this.ensureSession();
    const context: FolderContext = { kind: 'task', id: data.taskId };
    const rootId = this.roots().get(this.rootKey(data.companyId, context));
    if (!rootId) return; // Events never fetch contexts the user has not opened.
    const pending = [rootId];
    const seen = new Set<string>();
    const loadedChildren: string[] = [];
    while (pending.length > 0) {
      const id = pending.pop();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const folder = this.folders.get(id)();
      pending.push(...(folder?.subFolderIds ?? []));
      if (id !== rootId && this.contentVersions.has(id)) loadedChildren.push(id);
    }
    this.reloadRoot(context);
    loadedChildren.forEach(id => this.reload(id));
  }

  public getRoot(context: FolderContext): Signal<ExplorerFolder | undefined> {
    const session = this.ensureSession();
    const companyId = this.company();
    const key = this.rootKey(companyId, context);
    if (session && companyId && context.id && !this.states().has(key)) {
      this.fetch(key, this.http.getRoot(context), companyId, key);
    }
    return computed(() => {
      if (session !== this.sessionKey() || companyId !== this.company() || !this.states().get(key)?.loaded) {
        return undefined;
      }
      const id = this.roots().get(key);
      return id ? this.folders.get(id)() : undefined;
    });
  }

  public getById(folderId: string): Signal<ExplorerFolder | undefined> {
    const session = this.ensureSession();
    const companyId = this.company();
    const key = this.folderKey(companyId, folderId);
    if (session && companyId && folderId && !this.states().has(key)) {
      this.fetch(key, this.http.getById(folderId), companyId);
    }
    return computed(() =>
      session === this.sessionKey() && companyId === this.company() && this.states().get(key)?.loaded
        ? this.folders.get(folderId)()
        : undefined
    );
  }

  public getRootState(context: FolderContext): Signal<FolderLoadState> {
    this.ensureSession();
    return this.getState(this.rootKey(this.company(), context), this.company());
  }

  public getStateById(folderId: string): Signal<FolderLoadState> {
    this.ensureSession();
    return this.getState(this.folderKey(this.company(), folderId), this.company());
  }

  public getChildren(folderId: string): Signal<ExplorerFolder[]> {
    const folder = this.getById(folderId);
    return computed(() =>
      (folder()?.subFolderIds ?? []).map(id => this.folders.get(id)()).filter(child => child !== undefined)
    );
  }

  public getFiles(folderId: string): Signal<FileInfo[] | undefined> {
    const folder = this.getById(folderId);
    return computed(() => {
      const current = folder();
      return current
        ? current.fileIds.map(id => this.fileData.getById(id)()).filter(file => file !== undefined)
        : undefined;
    });
  }

  public reloadRoot(context: FolderContext): void {
    const session = this.ensureSession();
    const companyId = this.company();
    if (session && companyId && context.id) {
      const key = this.rootKey(companyId, context);
      this.fetch(key, this.http.getRoot(context), companyId, key);
    }
  }

  public reload(folderId: string): void {
    const session = this.ensureSession();
    const companyId = this.company();
    if (session && companyId && folderId) {
      const aliases = [...this.roots()].filter(([, id]) => id === folderId).map(([key]) => key);
      this.fetch(this.folderKey(companyId, folderId), this.http.getById(folderId), companyId, undefined, aliases);
    }
  }

  public renameAttachment(folderId: string, fileId: string, name: string): Observable<FolderFileDTO> {
    return this.mutate(
      () => this.fileHttp.renameAttachment(fileId, name),
      dto => {
        this.cacheSavedAttachment(fileId, dto);
        this.reload(folderId);
      }
    );
  }

  public moveAttachment(folderId: string, fileId: string, targetFolderId: string): Observable<FolderFileDTO> {
    return this.mutate(
      () => this.fileHttp.moveAttachment(fileId, targetFolderId),
      dto => {
        this.cacheSavedAttachment(fileId, dto);
        this.reload(folderId);
        if (targetFolderId !== folderId) this.reload(targetFolderId);
      }
    );
  }

  public deleteAttachment(folderId: string, fileId: string, taskId?: string): Observable<void> {
    return this.mutate(
      () => this.fileHttp.deleteAttachment(fileId),
      () => {
        this.fileData.removeLocalAttachment(fileId);
        this.reload(folderId);
        if (taskId) this.tasks.refreshTask(taskId);
      }
    );
  }

  private cacheSavedAttachment(fileId: string, dto: FolderFileDTO): void {
    if (dto.fileInFolderId !== fileId) throw new Error('The API returned a different attachment identity.');
    this.fileData.upsertLocalData(this.factory.produceFileDTO(dto));
  }

  public upload(folderId: string, upload: UploadData, taskId?: string): string {
    const session = this.ensureSession();
    const companyId = this.company();
    if (!session || !companyId) throw new Error('Folder uploads require an authenticated company context.');
    return this.fileData.uploadFile({ context: FileContext.folderFile, folderId, taskId }, upload, () => {
      if (session === this.sessionKey() && companyId === this.company()) this.reload(folderId);
    });
  }

  public setExternalLink(folderId: string, url: string | null): Observable<FolderDTO> {
    return this.mutate(
      () => this.http.setExternalLink(folderId, url),
      saved => {
        if (saved.id !== folderId) throw new Error('The API returned a different folder identity.');
        this.reload(folderId);
      }
    );
  }

  public initialize(context: FolderContext): Observable<FolderDTO> {
    return this.mutate(
      () => this.http.initialize(context),
      () => this.reloadRoot(context)
    );
  }

  public create(parentFolderId: string, name: string): Observable<FolderDTO> {
    return this.mutate(
      () => this.http.create(parentFolderId, name),
      () => this.reload(parentFolderId)
    );
  }

  public rename(folderId: string, name: string): Observable<FolderDTO> {
    return this.mutate(
      () => this.http.rename(folderId, name),
      folder => {
        this.reload(folderId);
        if (folder.parentFolderId) this.reload(folder.parentFolderId);
      }
    );
  }

  public delete(folderId: string, parentFolderId: string): Observable<void> {
    return this.mutate(
      () => this.http.delete(folderId),
      () => {
        const key = this.folderKey(this.company(), folderId);
        this.requests.get(key)?.unsubscribe();
        this.requests.delete(key);
        this.folders.delete(folderId);
        this.contentVersions.delete(folderId);
        this.states.update(states => {
          const next = new Map(states);
          next.delete(key);
          return next;
        });
        this.reload(parentFolderId);
      }
    );
  }

  private mutate<T>(source: () => Observable<T>, afterSave: (value: T) => void): Observable<T> {
    return defer(() => {
      const session = this.ensureSession();
      const companyId = this.company();
      if (!session || !companyId) {
        return throwError(() => new Error('Folder operations require an authenticated company context.'));
      }
      return source().pipe(
        filter(() => session === this.sessionKey() && companyId === this.company()),
        tap(afterSave),
        takeUntilDestroyed(this.destroyRef)
      );
    });
  }

  public upsertLocalData(dto: FolderDTO): Signal<ExplorerFolder> {
    this.ensureSession();
    const version = ++this.nextVersion;
    this.cacheMetadata(dto, version);
    if (this.contentVersions.has(dto.id)) {
      this.contentVersions.set(dto.id, version);
    }
    return this.folders.get(dto.id);
  }

  private getState(key: string, companyId: string | undefined): Signal<FolderLoadState> {
    const session = this.session;
    return computed(() =>
      session === this.sessionKey() && companyId === this.company()
        ? (this.states().get(key) ?? INITIAL_STATE)
        : INITIAL_STATE
    );
  }

  private fetch(
    key: string,
    source: Observable<FolderDTO | null>,
    companyId: string,
    rootKey?: string,
    aliases: string[] = []
  ): void {
    // getRoot/getById may be evaluated inside a computed (e.g. breadcrumbs, expanded rows); starting a request
    // there is an intentional side effect, so its loading-state write must not be attributed to that consumer.
    untracked(() => this.startFetch(key, source, companyId, rootKey, aliases));
  }

  private startFetch(
    key: string,
    source: Observable<FolderDTO | null>,
    companyId: string,
    rootKey: string | undefined,
    aliases: string[]
  ): void {
    const session = this.session;
    this.requests.get(key)?.unsubscribe();
    const version = ++this.nextVersion;
    this.requestVersions.set(key, version);
    this.setState(key, { loading: true, loaded: false }, version);
    aliases.forEach(alias => this.setState(alias, { loading: true, loaded: false }, version));
    const request = source
      .pipe(
        filter(() => session === this.sessionKey() && companyId === this.company()),
        map(dto => (dto ? this.cacheContent(dto, version) : undefined)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (this.requestVersions.get(key) === version) {
            this.requests.delete(key);
          }
        })
      )
      .subscribe({
        next: folder => {
          if (rootKey) {
            this.roots.update(roots => {
              const next = new Map(roots);
              if (folder) {
                next.set(rootKey, folder.id);
              } else {
                next.delete(rootKey);
              }
              return next;
            });
          }
          if (folder) {
            this.setState(this.folderKey(companyId, folder.id), { loading: false, loaded: true }, version);
          }
          this.setState(key, { loading: false, loaded: true }, version);
          aliases.forEach(alias => this.setState(alias, { loading: false, loaded: true }, version));
        },
        error: () => {
          if (session === this.sessionKey() && companyId === this.company()) {
            this.setState(key, { loading: false, loaded: false, error: 'files.explorer.load-error' }, version);
            aliases.forEach(alias =>
              this.setState(alias, { loading: false, loaded: false, error: 'files.explorer.load-error' }, version)
            );
          }
        }
      });
    if (!request.closed) {
      this.requests.set(key, request);
    }
  }

  private cacheContent(dto: FolderDTO, version: number): ExplorerFolder {
    if ((this.contentVersions.get(dto.id) ?? -1) > version) {
      return this.folders.get(dto.id)();
    }
    // Validate the complete batch before changing the shared cache; physical IDs are never a fallback.
    const files = (dto.files ?? []).map(file => this.factory.produceFileDTO(file));
    this.cacheMetadata(dto, version);
    files.forEach(file => this.fileData.upsertLocalData(file));
    const folder = this.factory.produce(dto);
    folder.fileIds = files.map(file => file.id);
    this.folders.upsert(folder);
    this.contentVersions.set(dto.id, version);
    return folder;
  }

  private cacheMetadata(dto: FolderDTO, version: number): void {
    if ((this.contentVersions.get(dto.id) ?? -1) > version) {
      return;
    }
    const folder = this.factory.produce(dto);
    const existing = this.folders.hasDataForId(dto.id) ? this.folders.get(dto.id)() : undefined;
    if (existing && this.contentVersions.has(dto.id)) {
      folder.fileIds = existing.fileIds;
      folder.fileCapabilities = existing.fileCapabilities;
      folder.subFolderIds = existing.subFolderIds;
    }
    this.folders.upsert(folder);
    dto.subFolders?.forEach(child => this.cacheMetadata(child, version));
  }

  private setState(key: string, state: FolderLoadState, version: number): void {
    if ((this.stateVersions.get(key) ?? -1) > version) {
      return;
    }
    this.stateVersions.set(key, version);
    this.states.update(states => new Map(states).set(key, state));
  }

  /**
   * Drops the cache when the user/company scope changes (logout, account or company switch). A token refresh
   * keeps the same scope, so in-flight reads and mutations still deliver their results.
   * Read paths can run inside a computed, so this never writes a signal: it swaps in fresh stores, and every
   * signal returned earlier is gated on the scope it was created in.
   */
  private ensureSession(): string | undefined {
    const current = this.sessionKey();
    if (current !== this.session) {
      this.requests.forEach(request => request.unsubscribe());
      this.requests.clear();
      this.requestVersions.clear();
      this.stateVersions.clear();
      this.contentVersions.clear();
      this.folders = new DataStore<ExplorerFolder>();
      this.roots = signal(new Map<string, string>());
      this.states = signal(new Map<string, FolderLoadState>());
      this.session = current;
    }
    return current;
  }

  private rootKey(companyId: string | undefined, context: FolderContext): string {
    return JSON.stringify([companyId, 'root', context.kind, context.id]);
  }

  private folderKey(companyId: string | undefined, folderId: string): string {
    return JSON.stringify([companyId, 'folder', folderId]);
  }
}
